import { Link } from "react-router-dom";
import industrialImage from "@/assets/industrial-smart-stock-cabina.webp.asset.json";
import retailImage from "@/assets/retail-smart-store-bigfish.webp.asset.json";
import medicalImage from "@/assets/medical-lab.png.asset.json";
import fieldBg from "@/assets/platform-field-bg.png.asset.json";
import { useTranslation } from "react-i18next";
import { withLang } from "@/lib/langPath";


const PixelArrow = () => (
  <svg viewBox="0 0 12 12" className="h-3.5 w-3.5" fill="currentColor" shapeRendering="crispEdges">
    <rect x="1" y="5" width="7" height="2" />
    <rect x="7" y="3" width="2" height="1" />
    <rect x="7" y="8" width="2" height="1" />
    <rect x="9" y="5" width="2" height="2" />
  </svg>
);

type Division = {
  num: string;
  kicker: string;
  sub: string;
  description: string;
  metric: string;
  href: string;
  cta: string;
  accent: string;
  image: string;
  imageAlt: string;
};

const PlatformDivisions = () => {
  const { t } = useTranslation();

  const divisions: Division[] = [
    {
      num: "01",
      kicker: t("sHome.platformDivisions.retailKicker"),
      sub: t("sHome.platformDivisions.retailSub"),
      description: t("sHome.platformDivisions.retailDesc"),
      metric: t("sHome.platformDivisions.retailMetric"),
      href: "/retail",
      cta: t("sHome.platformDivisions.retailCta"),
      accent: "#F2C4BC",
      image: retailImage.url,
      imageAlt: t("sHome.platformDivisions.retailAlt"),
    },
    {
      num: "02",
      kicker: t("sHome.platformDivisions.industrialKicker"),
      sub: t("sHome.platformDivisions.industrialSub"),
      description: t("sHome.platformDivisions.industrialDesc"),
      metric: t("sHome.platformDivisions.industrialMetric"),
      href: "/industrial",
      cta: t("sHome.platformDivisions.industrialCta"),
      accent: "#F0E4B8",
      image: industrialImage.url,
      imageAlt: t("sHome.platformDivisions.industrialAlt"),
    },
    {
      num: "03",
      kicker: t("sHome.platformDivisions.medicalKicker"),
      sub: t("sHome.platformDivisions.medicalSub"),
      description: t("sHome.platformDivisions.medicalDesc"),
      metric: t("sHome.platformDivisions.medicalMetric"),
      href: "/medical",
      cta: t("sHome.platformDivisions.medicalCta"),
      accent: "#B8EFC8",
      image: medicalImage.url,
      imageAlt: t("sHome.platformDivisions.medicalAlt"),
    },
  ];

  return (
    <section id="verticales" className="relative flex min-h-screen items-center overflow-hidden scroll-mt-20 text-black">
      <img
        src={fieldBg.url}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover object-center"
      />
      <div className="absolute inset-0 bg-black/10" aria-hidden="true" />
      <div className="relative w-full container mx-auto px-4 md:px-6 py-14 md:py-20">

        <h2
          className="max-w-none font-serif text-[1.75rem] sm:text-3xl md:text-4xl lg:text-5xl font-normal leading-[1.08] tracking-[-0.01em] text-black lg:whitespace-nowrap"
        >
          {t("sHome.platformDivisions.title")}
        </h2>
        <p
          className="mt-2 max-w-none text-sm md:text-base lg:text-lg text-black/60 leading-snug lg:whitespace-nowrap"

        >
          {t("sHome.platformDivisions.subtitle")}

        </p>

        <div className="mt-10 md:mt-12 grid gap-5 md:grid-cols-3 max-w-5xl mx-auto w-full">
          {divisions.map((d, i) => (
            <div
              key={d.kicker}
            >
              <Link
                to={withLang(d.href)}
                className="group flex h-full flex-col bg-black/25 backdrop-blur-sm border border-white/10 p-4 md:p-5 transition-all duration-500 ease-out hover:-translate-y-1.5 hover:bg-black/35"
              >
                <h3 className="text-2xl md:text-3xl font-normal" style={{ color: d.accent }}>
                  {d.kicker}
                </h3>
                <p
                  className="mt-1.5 font-dot text-xs md:text-sm tracking-wide"
                  style={{ color: d.accent }}
                >
                  {d.sub}
                </p>

                <div className="my-3 md:my-4 overflow-hidden">
                  <img
                    src={d.image}
                    alt={d.imageAlt}
                    loading="lazy"
                    className="h-40 sm:h-44 md:h-40 lg:h-44 w-full object-cover transition-all duration-700 ease-out lg:group-hover:scale-[1.04]"
                  />
                </div>



                <div className="grid grid-rows-[1fr] opacity-100 transition-all duration-500 ease-out lg:grid-rows-[0fr] lg:opacity-0 lg:group-hover:grid-rows-[1fr] lg:group-hover:opacity-100 lg:group-focus-visible:grid-rows-[1fr] lg:group-focus-visible:opacity-100">
                  <div className="overflow-hidden">
                    <span
                      className="block font-dot text-4xl md:text-5xl leading-none"
                      style={{ color: d.accent }}
                    >
                      {d.num}
                    </span>
                    <p className="mt-3 text-base md:text-lg text-white/75 leading-relaxed">
                      {d.description}
                    </p>
                    <p className="mt-2 text-sm md:text-base text-white/45">{d.metric}</p>
                  </div>
                </div>


                <span className="mt-auto pt-5 inline-flex items-center gap-2 text-base md:text-lg" style={{ color: d.accent }}>

                  <PixelArrow />
                  <span className="relative">
                    {d.cta}
                    <span
                      className="absolute -bottom-0.5 left-0 h-px w-full origin-left scale-x-0 transition-transform duration-500 ease-out group-hover:scale-x-100"
                      style={{ backgroundColor: d.accent }}
                    />
                  </span>
                </span>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PlatformDivisions;
